import { z } from 'zod';

// ── Phase 1 search schema (ConnectConsumerMatch) ───────────────────────────
export const kycOnlineSchema = z.object({
    idNumber: z.string().length(13, "ID Number must be 13 digits").regex(/^\d+$/, "ID Number must be numeric").optional().or(z.literal('')),
    passportNo: z.string().regex(/^[a-zA-Z0-9]+$/, "Passport Number must be alphanumeric").optional().or(z.literal('')),
    firstName: z.string().optional().or(z.literal('')),
    surname: z.string().optional().or(z.literal('')),
    birthDate: z.string().refine((val) => !val || new Date(val) <= new Date(), "Date of Birth cannot be in the future").optional().or(z.literal('')),
    voucherCode: z.string().optional().or(z.literal('')),
    yourReference: z.string().optional().or(z.literal('')),
}).superRefine((data, ctx) => {
    const idNumber = data.idNumber === '' ? undefined : data.idNumber;
    const passportNo = data.passportNo === '' ? undefined : data.passportNo;
    const firstName = data.firstName === '' ? undefined : data.firstName;
    const surname = data.surname === '' ? undefined : data.surname;
    const birthDate = data.birthDate === '' ? undefined : data.birthDate;

    const hasID = !!idNumber;
    const hasPassport = !!passportNo;
    const hasFullDetails = !!firstName && !!surname && !!birthDate;

    if (!hasID && !hasPassport && !hasFullDetails) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please provide either an ID Number OR Passport Number OR First Name, Surname, and Date of Birth",
            path: ['idNumber'],
        });
    }

    if (!hasID && !hasPassport && (firstName || surname || birthDate)) {
        if (!firstName) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "First Name is required", path: ['firstName'] });
        if (!surname) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Surname is required", path: ['surname'] });
        if (!birthDate) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Date of Birth is required", path: ['birthDate'] });
    }
});

// ── Phase 2 address schema (ConnectGetConsumerKYCOnline) ───────────────────
export const kycAddressSchema = z.object({
    addressLine1: z.string().min(1, "Address Line 1 is required"),
    addressLine2: z.string().min(1, "Address Line 2 is required"),
    addressLine3: z.string().optional().or(z.literal('')),
    addressLine4: z.string().optional().or(z.literal('')),
    postalCode: z.string().min(1, "Postal Code is required"),
});

// ── Types ──────────────────────────────────────────────────────────────────

export type KYCOnlinePayload = z.infer<typeof kycOnlineSchema>;

export interface KYCMatchItem {
    consumer_id: string | null;
    first_name: string | null;
    surname: string | null;
    id_number: string | null;
    birth_date: string | null;
    gender: string | null;
    enquiry_id: string;
    enquiry_result_id: string;
    reference: string | null;
}

export interface KYCMatchResponse {
    success: boolean;
    message: string;
    log_id: string;
    matches: KYCMatchItem[];
}

export interface KYCResultPayload {
    enquiryId: number;
    enquiryResultId: number;
    idNumber?: string;
    passportNo?: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3?: string;
    addressLine4?: string;
    postalCode: string;
    yourReference?: string;
    voucherCode?: string;
}

export interface KYCResponse {
    message: string;
    success: boolean;
    log_id: string;
    report_data: any;
}

// ── API calls ──────────────────────────────────────────────────────────────

const BASE_URL = '/verification/kyc-online';

export const kycOnlineService = {
    createPayload: (formData: z.infer<typeof kycOnlineSchema>): KYCOnlinePayload => ({
        ...formData,
    }),

    getMatch: async (payload: KYCOnlinePayload): Promise<KYCMatchResponse> => {
        const axios = (await import('../axiosConfig')).default;
        // Map frontend field names to backend field names
        const backendPayload = {
            idNo: payload.idNumber || undefined,
            passportNo: payload.passportNo || undefined,
            firstName: payload.firstName || undefined,
            surname: payload.surname || undefined,
            birthDate: payload.birthDate || undefined,
            voucherCode: payload.voucherCode || undefined,
            yourReference: payload.yourReference || undefined,
        };
        const response = await axios.post<KYCMatchResponse>(`${BASE_URL}/match`, backendPayload);
        return response.data;
    },

    getResult: async (payload: KYCResultPayload): Promise<KYCResponse> => {
        const axios = (await import('../axiosConfig')).default;
        const response = await axios.post<KYCResponse>(`${BASE_URL}/result`, payload);
        return response.data;
    },
};
